"use client";

import type { LibraryEntryItem } from "@/lib/api/api-client";
import { sharerName } from "@/lib/library/entry-copy";
import {
  entryOriginLabel,
  formatDayMonth,
  formatLastOpened,
  personInitial,
} from "@/lib/library/entry-copy";
import {
  journeyGlanceFromSummary,
  journeyGlanceLabel,
} from "@/lib/library/journey-glance";
import { ActionsMenu } from "./actions-menu";
import { PieceCard } from "./piece-card";
import { PieceMenu } from "./piece-menu";

/**
 * La carte d'une entrée de bibliothèque : la tuile du catalogue, telle quelle,
 * plus ce que le fait de la posséder ajoute.
 *
 * # C'est la même tuile, et c'est le point
 *
 * Le morceau qu'on ajoute doit avoir la forme du morceau qu'on a vu. Avant,
 * « Mes morceaux » était une liste de lignes de texte face à une grille de
 * cartes à silhouette : les morceaux perdaient leur figure au moment précis où
 * ils devenaient les nôtres, et on ne reconnaissait plus les siens.
 *
 * # Ce que la possession ajoute
 *
 * Une deuxième ligne sous la ligne de palier — l'état de travail, l'origine, la
 * dernière ouverture — et un bouton d'actions posé sur la tuile. Rien d'autre :
 * ni couleur par origine, ni badge, ni fond propre. Trois teintes de plus dans
 * une grille qui vit déjà de deux couleurs de main, pour une information qui ne
 * change rien à ce qu'on peut faire du morceau.
 *
 * Seul « partagé par quelqu'un » gagne une pastille, parce que c'est le seul
 * cas où l'origine est une personne.
 */

export function EntryCard({
  entry,
  onRemove,
  showSharedBy = false,
}: {
  entry: LibraryEntryItem;
  /** Absent : la carte n'offre pas le retrait (une ligne de playlist, par ex.). */
  onRemove?: () => void;
  /**
   * Nomme la personne qui a partagé, avec sa pastille d'initiale, et la date
   * d'entrée plutôt que la dernière ouverture.
   *
   * Réservé à « Partagé avec moi » : là-bas, *qui* est la seule chose qui
   * distingue une ligne de sa voisine, et « ouvert il y a 3 jours » ne dit rien
   * d'un morceau qu'on vient de recevoir.
   */
  showSharedBy?: boolean;
}) {
  return (
    <PieceCard
      piece={entry.piece}
      footer={<EntryLine entry={entry} showSharedBy={showSharedBy} />}
      action={
        <ActionsMenu label={`Actions sur « ${entry.title} »`}>
          {(close) => (
            <PieceMenu
              piece={entry.piece}
              entry={entry}
              close={close}
              onRemove={onRemove}
            />
          )}
        </ActionsMenu>
      }
    />
  );
}

/**
 * La deuxième ligne : l'état de travail en couleur d'accent, puis l'origine et
 * la dernière ouverture en gris.
 *
 * L'ordre n'est pas neutre. L'état de travail est la seule information qui
 * change ce qu'on va faire du morceau maintenant ; l'origine et la date
 * situent, elles ne décident de rien. La couleur suit la même hiérarchie.
 *
 * # La date s'efface devant l'état de travail
 *
 * Mesuré en rendu réel : à 200 px de large, « Phrase 7 sur 19 · Catalogue ·
 * ouvert aujourd'hui » se coupe au milieu de la date. Ce qui saute, c'est donc
 * la date — l'état de travail est plus récent qu'elle par construction (il a
 * été écrit *pendant* la dernière séance) et bien plus utile. Une carte sans
 * état de travail garde sa date, qui devient sa seule information de temps.
 */
export function EntryLine({
  entry,
  showSharedBy = false,
}: {
  entry: LibraryEntryItem;
  showSharedBy?: boolean;
}) {
  const glance =
    entry.practice === null ? null : journeyGlanceFromSummary(entry.practice);

  if (!entry.openable) {
    return (
      <span className="library-card-entry-text block truncate text-[11px] leading-4 text-text-muted">
        Ce fichier ne s'ouvre pas.
      </span>
    );
  }

  if (showSharedBy && entry.sharedBy !== null) {
    return (
      <span className="flex items-center gap-1.5 text-[11px] leading-4 text-text-muted">
        <PersonBadge name={sharerName(entry.sharedBy) ?? ""} />
        <span className="library-card-entry-text truncate">
          {entryOriginLabel(entry)} · {formatDayMonth(entry.addedAt)}
        </span>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-[11px] leading-4 text-text-muted">
      {glance !== null && (
        <>
          <span className="shrink-0" style={{ color: "var(--color-accent-bright)" }}>
            {journeyGlanceLabel(glance)}
          </span>
          <span aria-hidden="true" className="opacity-55">
            ·
          </span>
        </>
      )}
      <span className="library-card-entry-text truncate">
        {entryOriginLabel(entry)}
        {glance === null && ` · ouvert ${formatLastOpened(entry.lastOpenedAt)}`}
      </span>
    </span>
  );
}

/**
 * Une initiale dans un rond : le seul « visage » du produit.
 *
 * Décorative — le nom est écrit juste à côté, en toutes lettres. La lire à voix
 * haute ferait entendre « C, Partagé par Camille ».
 */
export function PersonBadge({
  name,
  size = 22,
}: {
  name: string;
  size?: number;
}) {
  return (
    <span
      aria-hidden="true"
      className="grid shrink-0 place-items-center rounded-full font-mono font-semibold text-accent-bright"
      style={{
        width: size,
        height: size,
        fontSize: size <= 24 ? 10 : 12,
        backgroundColor: "var(--color-accent-subtle)",
        border: "1px solid var(--color-violet-glow)",
      }}
    >
      {personInitial(name)}
    </span>
  );
}
